/**
 * Severity Selector Component
 * 
 * PASS / CAUTION / FAIL / NA button group with color coding
 */

import { cn } from '@/lib/utils';
import { Check, AlertTriangle, X, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { InspectionSeverity } from '@/hooks/useInspectionExecution';

interface SeveritySelectorProps {
  value: InspectionSeverity | null;
  onChange: (severity: InspectionSeverity) => void;
  allowedSeverities?: InspectionSeverity[];
  disabled?: boolean;
  size?: 'sm' | 'default';
}

const SEVERITY_CONFIG: Record<InspectionSeverity, {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  activeClass: string;
  hoverClass: string;
}> = {
  PASS: {
    label: 'Pass',
    icon: Check,
    activeClass: 'bg-green-500 text-white border-green-500 hover:bg-green-600',
    hoverClass: 'hover:border-green-300 hover:text-green-600',
  },
  CAUTION: {
    label: 'Caution',
    icon: AlertTriangle,
    activeClass: 'bg-yellow-500 text-white border-yellow-500 hover:bg-yellow-600',
    hoverClass: 'hover:border-yellow-300 hover:text-yellow-600',
  },
  FAIL: {
    label: 'Fail',
    icon: X,
    activeClass: 'bg-red-500 text-white border-red-500 hover:bg-red-600',
    hoverClass: 'hover:border-red-300 hover:text-red-600',
  },
  NA: {
    label: 'N/A',
    icon: Minus,
    activeClass: 'bg-muted text-muted-foreground border-muted',
    hoverClass: 'hover:border-muted-foreground/50',
  },
};

const DEFAULT_ALLOWED: InspectionSeverity[] = ['PASS', 'CAUTION', 'FAIL', 'NA'];

export function SeveritySelector({
  value,
  onChange,
  allowedSeverities = DEFAULT_ALLOWED,
  disabled = false,
  size = 'default',
}: SeveritySelectorProps) {
  return (
    <div className="flex gap-1">
      {allowedSeverities.map((severity) => {
        const config = SEVERITY_CONFIG[severity];
        const Icon = config.icon;
        const isSelected = value === severity;

        return (
          <Button
            key={severity}
            type="button"
            variant="outline"
            size={size === 'sm' ? 'sm' : 'default'}
            disabled={disabled}
            onClick={() => onChange(severity)}
            className={cn(
              'transition-all',
              size === 'sm' ? 'px-2 py-1' : 'px-3 py-2',
              isSelected ? config.activeClass : config.hoverClass,
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Icon className={cn('h-4 w-4', size === 'sm' ? 'mr-1' : 'mr-1.5')} />
            <span className={size === 'sm' ? 'text-xs' : 'text-sm'}>
              {config.label}
            </span>
          </Button>
        );
      })}
    </div>
  );
}
